import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { Info } from 'react-feather';
import { useDefaultMaxLengthFieldError, useDisclosure } from '@/hooks';
import { UpdateInvoiceAdditionalDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import {
  InvoiceOriginReferenceEntity,
  InvoiceSituation,
} from '@/core/domain/invoice/invoices/invoices.enums';
import CompanyTextEditor from '@/components/Companies/CompanyTextEditor';
import { useInvoiceFormContext } from '../InvoiceFormProvider';
import { FloatingFormItem } from '@/components/_common';

type Props = {
  form: FormInstance<UpdateInvoiceAdditionalDTO>;
  situation?: InvoiceSituation;
};

const InvoiceAdditionalDataForm: React.FC<Props> = ({ form }) => {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;

  const {
    disabledFields,
    isNfe,
    isNfc,
    invoice: { data: invoice },
  } = useInvoiceFormContext();

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const [isModalVisible, setIsModalVisible] = useDisclosure();

  const workordersReference = invoice?.originReference?.filter(
    (item) => item.entity === InvoiceOriginReferenceEntity.WORK_ORDER,
  );

  const hasWorkorder = !!workordersReference?.length;

  const hasTag =
    invoice?.taxAdditionalData?.includes('@{') ||
    invoice?.companyAdditionalData?.includes('@{') ||
    invoice?.description?.includes('@{');

  const hasTagFormOfPayment =
    invoice?.taxAdditionalData?.includes('@{formaDePagamento}') ||
    invoice?.companyAdditionalData?.includes('@{formaDePagamento}') ||
    invoice?.description?.includes('@{formaDePagamento}');

  return (
    <>
      <Form form={form} name="invoiceAditionalData">
        <Row gutter={[24, 24]}>
          {(isNfe || isNfc) && (
            <>
              <Col span={24}>
                <Title level={5}>Informações de Compras</Title>
              </Col>

              <Col span={24} md={8}>
                <FloatingFormItem
                  name="commitmentNote"
                  label="Nota de empenho"
                  rules={[defaultMaxLenghtFieldError(22)]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={8}>
                <FloatingFormItem
                  name="orderBuy"
                  label="Pedido"
                  rules={[defaultMaxLenghtFieldError(60)]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={8}>
                <FloatingFormItem
                  name="contract"
                  label="Contrato"
                  rules={[defaultMaxLenghtFieldError(60)]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            </>
          )}

          {hasTag && !hasWorkorder && !hasTagFormOfPayment && (
            <Alert
              style={{ marginBottom: 24 }}
              type="warning"
              message={<strong>Tradução dos dados adicionais</strong>}
              description={`As tags utilizadas nos dados adicionais não serão traduzidas na transmissão da nota, poís esta nota não tem referência com um orçamento. Exceto a tag 'formaDePagamento', pois a mesma é buscada da aba 'condições de pagamento'.`}
            />
          )}

          <Col md={24}>
            <Title level={5}>
              {intl({
                id: 'invoice.createadditionalData.additionalPhysicalInformation',
              })}
            </Title>

            <Form.Item name="taxAdditionalData">
              <CompanyTextEditor
                disabledSelect={disabledFields}
                removeOptionalTags={!hasWorkorder}
              />
            </Form.Item>
          </Col>

          <Col md={24}>
            <Title level={5}>
              <Space>
                {intl({
                  id: 'invoice.createadditionalData.AdditionalTaxpayerInformation',
                })}

                {hasWorkorder && (
                  <AntdFeatherIcon
                    size={20}
                    icon={<Info />}
                    onClick={() => setIsModalVisible.toggle()}
                  />
                )}
              </Space>
            </Title>

            <Form.Item name="companyAdditionalData">
              <CompanyTextEditor
                disabledSelect={disabledFields}
                removeOptionalTags={!hasWorkorder}
              />
            </Form.Item>
          </Col>
        </Row>

        <Modal
          title={intl({ id: 'invoice.createadditionalData.modal.title' })}
          open={isModalVisible}
          onCancel={() => setIsModalVisible.toggle()}
          okButtonProps={{ hidden: true }}
        >
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.1' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.2' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.3' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.4' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.5' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.6' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.7' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.8' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.9' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.10' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.11' })}</p>
          <p>{intl({ id: 'invoice.createadditionalData.modal.title.12' })}</p>
        </Modal>
      </Form>
    </>
  );
};

export default InvoiceAdditionalDataForm;
